import { CalendarClock } from 'lucide-react';

import type { PrazoCalculo } from '@/api/client';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { diasDoPrazo, ehPresumido, providenciaDoPrazo } from '@/lib/prazos/presumido';
import { rotuloDoVencimento, type TomDoVencimento } from '@/lib/prazos/vencimento';
import { cn } from '@/lib/utils';

/** As cores do selo por tom — as mesmas na intimação e na coluna do Acervo. */
export const CORES_DO_VENCIMENTO: Record<TomDoVencimento, string> = {
  vencido: 'border-destructive/40 text-destructive',
  hoje: 'border-destructive/40 bg-destructive/5 text-destructive',
  proximo: 'border-[var(--color-gold-border)] bg-[var(--color-gold-soft)] text-foreground',
  adiante: 'text-muted-foreground',
};

/**
 * O vencimento calculado, na linha da intimação. Estimado (sem o lugar completo)
 * leva borda tracejada e o "~": a data pode ser um pouco mais longa no tribunal.
 */
export function SeloDeVencimento({ calculo }: { calculo: PrazoCalculo }) {
  const rotulo = rotuloDoVencimento(calculo.vencimento, calculo.estimado);
  // O mesmo selo para o prazo do texto e o da lei: para quem lê a lista, os dois são
  // "um prazo a conferir". Só o tooltip diz de onde veio o número.
  const daLei = ehPresumido(calculo);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className={cn(
            'shrink-0 cursor-help gap-1 text-[11px]',
            CORES_DO_VENCIMENTO[rotulo.tom],
            calculo.estimado && 'border-dashed',
          )}
        >
          <CalendarClock className="h-3 w-3" />
          {rotulo.curto}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs space-y-1.5">
        {calculo.data_fixada ? (
          <p className="text-xs leading-relaxed">
            Data fixada na própria intimação, sem contagem de dias. Confira no tribunal.
          </p>
        ) : daLei ? (
          <p className="text-xs leading-relaxed">
            {providenciaDoPrazo(calculo)}: {diasDoPrazo(calculo.dias, calculo.regime)}
            {calculo.em_dobro ? ', em dobro' : ''} ({calculo.dispositivo}). A intimação não diz o
            prazo: é o da lei para o ato, contado pelo sistema a partir da disponibilização no
            DJEN. Confira no tribunal.
          </p>
        ) : (
          <p className="text-xs leading-relaxed">
            {calculo.dias} {calculo.regime === 'corridos' ? 'dias corridos' : 'dias úteis'}
            {calculo.em_dobro ? ', em dobro' : ''}, contados pelo sistema a partir da
            disponibilização no DJEN. Confira no tribunal.
          </p>
        )}
        {calculo.estimado && calculo.motivo_estimado && (
          <p className="text-xs leading-relaxed">{calculo.motivo_estimado}</p>
        )}
        {calculo.aviso_regime && <p className="text-xs leading-relaxed">{calculo.aviso_regime}</p>}
      </TooltipContent>
    </Tooltip>
  );
}
